// userWheelFrame.js — build a PER-USER Wheel-Portrait-Frame ring from that user's
// OWN primary/generated wheel segments. Gordon law `all-avatars-wheel-frames`
// (refined 2026-08-12): each avatar frame renders THAT USER'S own wheel, unique per
// person, IDENTICAL on every surface (chat/gallery/notifications/header) — the frame
// is tied to the PERSON, not the place. The default on-brand SVG
// (assets/images/backgroundWheelProfile.svg) is the fallback ONLY when a user has
// no wheel.
//
// Reuses the canonical brand-wheel geometry (BrandWheel/brandWheelInner — G-19) so
// there is NO hand-rolled donut: we feed it the user's real segment colours + spans
// (normalised value -> degrees) instead of the brand demo palette. A white backing
// circle makes the parallel-edged gaps read as white hairlines on any surface
// (white-segment-hairline law).
import { brandWheelInner } from "../BrandWheel/brandWheel.js";

const HEX_RE = /^#([0-9a-f]{3,8})$/i;

/**
 * The segment's own hex, from whichever key the payload actually carries it in.
 *
 * ISP-9999 (Gordon, 2026-08-29 — 4th report of "every avatar frame looks the same").
 * `GET /api/v2/wheel/primary/?user=<id>` serializes a segment's `color` from
 * `category.color.outer_color_code` (Backend
 * yuga_wheel/api/v2/serializers/activity.py) — it is the color of the segment's
 * CATEGORY, not the segment. A segment with no category therefore ships
 * `color: null` and carries its real hex in `segment_color`, which is how every
 * modern (category-less) wheel is coloured. Reading `color` alone made all of
 * them fall through to BRAND_WHEEL_PALETTE, so every such user rendered the SAME
 * ring. Measured live on web-stg bundle main.a4254783.js, 2026-08-29: 9 of the 10
 * avatars in /messages shared one identical frame SVG (sha1 dfb5e31e52, fills
 * exactly BRAND_WHEEL_PALETTE); the single exception was the one user whose wheel
 * still uses categories.
 *
 * `color` keeps precedence so no wheel that renders correctly today changes.
 */
export function segmentHex(s) {
  if (!s) return null;
  const candidates = [s.color, s.segment_color, s.segmentColor];
  for (const c of candidates) {
    if (typeof c === "string" && HEX_RE.test(c)) return c;
  }
  return null;
}

// djb2 — cheap, deterministic, no crypto dependency (runs in every browser this
// renders in). Only needs to scatter a per-wheel seed across a hue circle, not
// resist attack.
function hashSeed(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return h >>> 0;
}

/**
 * A colour for a segment that carries NO hex at all, derived from the wheel's own
 * identity so two different colourless wheels never resolve to the same ring.
 *
 * ISP-9999 (Gordon, 5th report of "every avatar frame looks the same", 2026-09-16).
 * `BRAND_WHEEL_PALETTE[i % length]` (the old fallback) is PURELY POSITIONAL — it
 * takes no input from the wheel or the user at all, so ANY two wheels with the same
 * segment count and no colour data (a wallpaper/AI-image-fill wheel with no
 * representative hex — `fill.type: "image"`, `color`/`segment_color` both null; a
 * real, currently-live shape, e.g. user 12675's own primary wheel 6378 on app-dev)
 * render the IDENTICAL ring. PR#4794 (2026-08-30) fixed reading `segment_color` but
 * did not touch this branch, so it still reproduces the complaint.
 * Hashing the wheel's own segment ids (a DB primary key, globally unique — no two
 * different wheels share one) into the hue keeps the on-brand saturation/lightness
 * of `BRAND_WHEEL_PALETTE` while making a same-shape collision as unlikely as a hash
 * collision instead of guaranteed.
 */
function seededFallbackColor(seed, index) {
  const hue = hashSeed(`${seed}:${index}`) % 360;
  return `hsl(${hue}, 68%, 63%)`;
}

/**
 * @param {Array<{color?:string, segment_color?:string, value?:number}>} segments  the user's wheel_segments
 * @returns {string|null} an <svg> string for the user's ring, or null when the user
 *   has no usable wheel (caller falls back to the default frame asset).
 */
export function userWheelFrameSVG(segments) {
  if (!Array.isArray(segments) || segments.length < 1) return null;

  // Normalise each segment's `value` to a degree span summing to 360 so the ring is
  // a full circle regardless of the raw value scale. Zero/absent values fall back to
  // equal spans so a wheel with unset values still renders a complete ring.
  const vals = segments.map((s) => {
    const v = Number(s && s.value);
    return Number.isFinite(v) && v > 0 ? v : 0;
  });
  const total = vals.reduce((a, b) => a + b, 0);
  const spans =
    total > 0
      ? vals.map((v) => (v > 0 ? (v / total) * 360 : 0))
      : segments.map(() => 360 / segments.length);
  // guard: if normalisation produced any zero spans (mixed set/unset values), even them out
  const spansSafe = spans.some((s) => s <= 0.01)
    ? segments.map(() => 360 / segments.length)
    : spans;

  // Per-wheel seed for segments with no colour at all — the wheel's own segment
  // ids, which are unique per wheel (so per user), not a fixed brand rotation.
  const seed = segments.map((s) => (s && s.id != null ? s.id : "")).join(",");
  const colors = segments.map((s, i) => segmentHex(s) || seededFallbackColor(seed, i));

  const inner = brandWheelInner({
    spans: spansSafe,
    colors,
    gap: 4,
    ratio: 0.75, // brand Portrait ratio — do not change
    disc: true,
  });

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" ` +
    `viewBox="0 0 120 120" fill="none" aria-hidden="true" style="display:block">` +
    `<circle cx="60" cy="60" r="57" fill="#ffffff"/>${inner}</svg>`
  );
}

/** data-URI form for use as a CSS background-image, mirroring the default asset URL. */
export function userWheelFrameDataUri(segments) {
  const svg = userWheelFrameSVG(segments);
  return svg ? "data:image/svg+xml;utf8," + encodeURIComponent(svg) : null;
}
